import React from 'react';
import { Package, Shield, Settings } from 'lucide-react';
import { AuthService } from '../auth/AuthService';

interface NavigationHeaderProps {
  onNavigate: (page: string) => void;
  currentUser: any;
}

export const NavigationHeader: React.FC<NavigationHeaderProps> = ({ onNavigate, currentUser }) => {
  return (
    <header className="bg-gradient-to-r from-burgundy-700 via-burgundy-800 to-burgundy-900 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => onNavigate('landing')}>
              <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg border border-white/20">
                <Package className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">Inventory Manager</h1>
                <p className="text-sm text-white/80">{currentUser?.organization?.display_name || 'System Administration'}</p>
              </div>
            </div>
          </div>

          {/* User Info and Actions */}
          <div className="flex items-center space-x-4">
            {currentUser && (
              <>
                {/* User info */}
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="text-white text-sm font-medium">
                      {currentUser.full_name || currentUser.email || 'User'}
                    </p>
                    <p className="text-white/70 text-xs">
                      {currentUser.organization?.display_name || 'owner'}
                    </p>
                  </div>
                  
                  {/* Google profile picture - with crossorigin and referrer policy */}
                  {currentUser.picture ? (
                    <img 
                      src={currentUser.picture} 
                      alt={currentUser.full_name || currentUser.email || 'User'}
                      className="w-10 h-10 rounded-full border-2 border-white/30 bg-white/20"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const initials = (currentUser.full_name || currentUser.email || 'U').charAt(0).toUpperCase();
                        const fallback = document.createElement('div');
                        fallback.className = 'w-10 h-10 rounded-full border-2 border-white/30 bg-white/20 flex items-center justify-center text-white font-semibold';
                        fallback.textContent = initials;
                        target.parentNode?.replaceChild(fallback, target);
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full border-2 border-white/30 bg-white/20 flex items-center justify-center text-white font-semibold">
                      {(currentUser.full_name || currentUser.email || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Settings button - visible when user can manage tokens */}
                {currentUser.permissions?.can_manage_tokens && (
                  <button
                    onClick={() => onNavigate('settings')}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 transition-colors text-sm font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                )}

                {/* Admin button - always visible for admin users with better styling */}
                {currentUser.is_admin && (
                  <button
                    onClick={() => onNavigate('admin')}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg border border-white/30 transition-colors text-sm font-medium"
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                )}
              </>
            )}
            
            {/* Sign out button */}
            <AuthService 
              variant="header" 
              showUserInfo={false}
            />
          </div>
        </div>
      </div>
    </header>
  );
};